import type { StatusTier } from "@/components/ui/badge"

import type { EmailStatus } from "../messages/types"

/**
 * Three-state on purpose. `null` means "cannot tell" — a DKIM selector we do
 * not hold, say — which is a different fact from a failing check and must not
 * render as one.
 */
export type CheckState = boolean | null

export interface Transport {
  rails_env: string
  provider: string
  provider_source: string
  delivery_method: string
  matches_matrix: boolean
  live_provider: boolean
  from: string
  reply_to: string
  link_host: string | null
  link_protocol: string | null
  link_url: string | null
  message_defaults: Record<string, string> | null
  live_route: string | null
  sandbox_route: string | null
}

export interface CredentialRow {
  key: string
  present: boolean
  /** Whether this provider/environment uses the credential at all. */
  applicable: boolean
  breaks: string
  detail_when_inapplicable: string | null
}

export interface AllowlistState {
  source: string
  applies: boolean
  entries: string[]
  count: number
  empty: boolean
  meaning: string
}

interface Check {
  ok: CheckState
  detail: string
}

export interface DomainReport {
  domain: string
  role: string
  available: boolean
  spf: (Check & { value: string | null }) | null
  dkim: (Check & { selector: string | null }) | null
  dmarc:
    | (Check & {
        value: string | null
        inherited: boolean
        policy: string | null
        rua: string | null
      })
    | null
}

export interface Deliverability {
  checked_at: string
  report_mailbox: string
  domains: DomainReport[]
}

export interface LedgerReport {
  total: number
  counts: Record<EmailStatus, number>
  awaiting_webhook: {
    count: number
    oldest_sent_at: string | null
    oldest_age_seconds: number | null
    oldest_id: number | null
  }
}

export interface WebhookReport {
  /** Whether Postmark can POST to this environment at all — false where the
   * server has no public URL (development), making `sent` terminal. */
  expected: boolean
  total: number
  last_received_at: string | null
  last_record_type: string | null
  count_24h: number
  unmatched_24h: number
  by_record_type: Record<string, number>
  retention_days: number
  never_received: boolean
}

export interface SuppressionReport {
  total: number
  by_reason: Record<string, number>
  recent: {
    address: string
    reason: string
    suppressed_at: string
    evidence_keys: string[]
  }[]
}

export interface QueueReport {
  available: boolean
  queued: number
  running: number
  discarded_24h: number
  finished_24h: number
}

export interface HealthReport {
  transport: Transport
  credentials: CredentialRow[]
  allowlist: AllowlistState
  deliverability: Deliverability
  ledger: LedgerReport
  webhooks: WebhookReport
  suppressions: SuppressionReport
  queue: QueueReport
}

export interface DryRunResult {
  address: string
  normalised: string
  provider: string
  suppressed: boolean
  suppression: {
    reason: string
    suppressed_at: string
    evidence_keys: string[]
  } | null
  allowlist_applies: boolean
  allowlisted: boolean | null
  outcome: "suppressed" | "live" | "sandbox" | "preview" | "test" | "unknown"
  explanation: string
}

export const OUTCOME_COLOR: Record<DryRunResult["outcome"], StatusTier> = {
  live: "confirmed",
  sandbox: "caution",
  suppressed: "caution",
  preview: "neutral",
  test: "neutral",
  unknown: "neutral",
}

export const OUTCOME_LABEL: Record<DryRunResult["outcome"], string> = {
  live: "Live",
  sandbox: "Sandbox",
  suppressed: "Suppressed",
  preview: "Preview",
  test: "Test",
  unknown: "Unknown",
}

export interface Triggers {
  available: boolean
  reason: string | null
  smoke_blockers: string[]
}

export interface Links {
  postmark_activity: string
  mailtrap_inbox: string
  good_job: string | null
  render_web: string
  render_web_logs: string
  render_worker: string
  render_worker_logs: string
  jira_epic: string
  jira_ticket: string
  ledger: string
}
